import { faAnglesUpDown /* , faChevronDown, faGear, faStar */ } from '@fortawesome/pro-regular-svg-icons';
import { faBrowser } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { sourTwMerge, Tooltip } from '@plaidev/sour-react';
import { type Dispatch, type ReactNode, type SetStateAction, useEffect, useRef, useState } from 'react';
import { resolveQantProductList, resolveSpeakNavigation, resolveVocNavigation } from '../config/qantProducts.js';
import type { LocalizedText } from '../config/type.js';
import type { GlobalNaviDataV2, ProductKey } from '../contract/index.js';
import { findActiveMenuName, usePathname } from '../internal/activeMenu.js';
import { resolveKarteOnlyProjectSwitchHref } from '../internal/karteOnlyProjectSwitchHref.js';
import { normalize } from '../internal/normalize.js';
import { createHrefBuilder, resolveMenuHref, resolveProductLandingHref, resolveProjectSwitchLandingHref } from '../internal/urls.js';
import type { MenuItem, ProductMenu } from '../types/menuModel.js';
import { L1Icon } from './icons.js';
import { MenuGroupList } from './MenuGroupList.js';
import { ProductEnvironmentBadge } from './ProductEnvironmentBadge.js';
import { ProductSwitcherPopover } from './ProductSwitcherPopover.js';
import { ProjectSwitcherPopover } from './ProjectSwitcherPopover.js';
import { SidebarShell } from './SidebarShell.js';
import { type GlobalNaviGrantActions, UserAccountPopover } from './UserAccountPopover.js';

type Locale = 'ja' | 'en';

const tx = (text: LocalizedText, locale: Locale): string => text[locale];

const VOC_ENVIRONMENT_LABELS = {
  new: { ja: '（新環境）', en: ' (New environment)' },
  old: { ja: '（旧環境）', en: ' (Old environment)' },
} as const satisfies Record<'new' | 'old', LocalizedText>;

/**
 * hover で展開するまでの待ち時間 (ms)。
 * ナビの上をポインタが素通りしただけで展開しないよう、意図的な滞留を待つ。
 */
const HOVER_EXPAND_DELAY_MS = 200;

/**
 * SPA 遷移ハンドラ。`href` は package が data.selfPlatform / current.projectIds を加味して
 * 解決済みのもの。host が SPA router で扱える場合は true を返すと <a> の既定遷移を抑止する。
 */
export type OnNavigate = (href: string, event: React.MouseEvent<HTMLAnchorElement>) => boolean | undefined;

export type GlobalNaviProps = {
  /**
   * backend で統合済みの GlobalNaviDataV2 (data-model.md (3) V2)。
   * `<GlobalNavi>` はこの data のみを真実のソースとして描画する。
   */
  data: GlobalNaviDataV2;
  /** 表示中のプロダクト (ホスト側で決定)。data.selfPlatform 以外のキーも許容する (cross-domain 表示)。 */
  currentProduct: ProductKey;
  /**
   * 現在の pathname。省略時は package が window.location.pathname を `useSyncExternalStore` で
   * 監視する。host 側が SPA router (TanStack Router など) の pathname を持っている場合は
   * 明示指定したほうが余計な subscribe を避けられる。
   */
  pathname?: string;
  /** SPA 遷移ハンドラ。指定しない場合は <a> の既定遷移にフォールバック。 */
  onNavigate?: OnNavigate;
  /**
   * プロダクト切替時に呼ばれる side-effect hook (遷移制御ではない)。
   * 「project ごとの最新プロダクト」など、host 側に永続化したい状態がある場合に使う。
   * 遷移自体は package が `window.location` で行う。
   */
  onProductChange?: (productKey: ProductKey) => void;
  /**
   * プロジェクト切替時に呼ばれる hook。指定時は遷移制御を host に委譲する。
   * 未指定時は package が selfPlatform に応じた既定遷移を行う。
   * QANT id がある行だけ呼ばれる（KARTE-only 行は package が別タブで開く）。
   */
  onProjectSwitch?: (params: { projectId: string; organizationId: string }) => void;
  /**
   * QANT ホスト (auth-lib-front) のみ渡す。grant メニューの mutation / ポーリング / ダイアログ用。
   * 表示 eligibility は data.user.grants が担う。KARTE ホストでは省略（メニューは disabled 表示のみ）。
   */
  grantActions?: GlobalNaviGrantActions;
  /**
   * normalize で導出された ProductMenu[] に対する後段の変換 hook。
   * 静的 config では表現できない動的メニュー注入 (例: neuro の agentId を埋めた
   * `/neuro/agents/detail/<sub>?id=<agentId>` 等) に使う escape hatch。
   * 通常は不要 (純粋データだけで描画される)。
   */
  transformProducts?: (products: readonly ProductMenu[]) => readonly ProductMenu[];
  /** プロダクトヘッダーとメニューリストの間に挿入するスロット (neuro の AgentSwitcher 等)。 */
  subHeader?: ReactNode | ((params: { expanded: boolean; setIsMenuOpened: Dispatch<SetStateAction<boolean>> }) => ReactNode);
  locale?: Locale;
  /**
   * ログアウト項目の注釈に出す「詳細はこちら」のリンク先。
   * 未指定なら QANT サポートサイトの該当記事。
   */
  logoutHelpHref?: string;
};

/**
 * Karte / QANT 共通グローバルナビゲーション。
 *
 * - 表示に必要なすべての情報は `data: GlobalNaviDataV2` (backend 統合済み) から取得する。
 * - href / cross-domain / プロジェクト指定クエリ (qant: `?pid=` / karte: `?project=`) は
 *   data.selfPlatform + data.current?.projectIds から package 内部で組み立てる。
 * - `current` 未確定でも account 一覧のプロジェクト切替 Popover は開ける。
 * - 各プロダクトのメニュー定義は package 内の static config を使う。
 *
 * 設計: packages/global-navi/docs/data-model.md
 */
export function GlobalNavi({
  data,
  currentProduct,
  pathname: pathnameProp,
  onNavigate,
  onProductChange,
  onProjectSwitch: onProjectSwitchProp,
  grantActions,
  transformProducts,
  subHeader,
  locale = 'ja',
  logoutHelpHref,
}: GlobalNaviProps): ReactNode {
  const [isPointerOver, setIsPointerOver] = useState(false);
  const [isFocusWithin, setIsFocusWithin] = useState(false);
  const [isMenuOpened, setIsMenuOpened] = useState(false);
  const [isSwitcherOpen, setIsSwitcherOpen] = useState(false);
  // mouseenter と focus のイベント順序に依存しないよう ref で同期追跡する
  const isPointerOverRef = useRef(false);
  const hoverExpandTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHoverExpandTimer = () => {
    if (hoverExpandTimerRef.current !== null) {
      clearTimeout(hoverExpandTimerRef.current);
      hoverExpandTimerRef.current = null;
    }
  };

  useEffect(
    () => () => {
      if (hoverExpandTimerRef.current !== null) clearTimeout(hoverExpandTimerRef.current);
    },
    [],
  );

  const expanded = isPointerOver || isFocusWithin || isMenuOpened || isSwitcherOpen;

  // (5) ViewModel への整形 (package 内部)。
  const view = normalize({
    data,
    staticProducts: resolveQantProductList(data.current?.products ?? [], {
      selfPlatform: data.selfPlatform,
      projectIds: data.current?.projectIds,
    }),
  });
  // 動的メニュー注入 (neuro の agentId など) の escape hatch。
  const products = transformProducts ? transformProducts(view.products) : view.products;

  // pathname 監視 (host が明示指定すればそれを優先)。useSyncExternalStore は必ず呼ぶ必要があるため
  // 明示時も hook 呼び出し自体は維持し、戻り値だけ捨てる。
  const observedPathname = usePathname();
  const pathname = pathnameProp ?? observedPathname;

  // currentProduct の ProductMenu を引く。currentProduct が selfPlatform 側に無い場合
  // (= cross-domain の product を表示中) は products 先頭にフォールバック。
  const productByName = new Map(products.map((p) => [p.name, p]));
  const currentProductMenu = productByName.get(currentProduct) ?? products[0];

  const selfProjectId = data.current?.projectIds[data.selfPlatform];
  const buildHref = createHrefBuilder({
    selfPlatform: data.selfPlatform,
    ...(selfProjectId ? { projectId: selfProjectId } : {}),
    projectIds: data.current?.projectIds,
    platformOrigins: data.platformOrigins,
  });
  const productNavigation =
    currentProductMenu?.name === 'voc' || currentProductMenu?.name === 'speak'
      ? (currentProductMenu.name === 'voc' ? resolveVocNavigation : resolveSpeakNavigation)(data.current?.products ?? [], {
          selfPlatform: data.selfPlatform,
          projectIds: data.current?.projectIds,
        })
      : undefined;
  const showProductEnvironment = productNavigation?.showEnvironmentBadge === true;
  const productEnvironment = data.selfPlatform === 'qant' ? 'new' : 'old';

  const activeMenuName = currentProductMenu ? findActiveMenuName(currentProductMenu, pathname) : undefined;

  const hrefResolver = (menu: MenuItem): string => resolveMenuHref(menu, buildHref);

  const onMenuClick = (menu: MenuItem, event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!onNavigate) return;
    // modifier キー付き / 中クリックはブラウザ標準（新規タブ等）に任せる
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    if (menu.options?.openAnotherTab) return; // target=_blank は anchor 任せ
    if (!menu.path) return;
    const { href, crossDomain } = buildHref(menu.path);
    if (crossDomain) return; // 別 host は anchor 任せ (host 側 SPA router の対象外)
    const handled = onNavigate(href, event);
    if (handled) event.preventDefault();
  };

  const onSelectProduct = (product: ProductMenu) => {
    // 外部リンクプロダクト (KARTE 本体等) は <a> が遷移 (新規タブ) を担うので何もしない。
    if (product.link) return;
    const landingHref = resolveProductLandingHref(product, buildHref);
    if (!landingHref) return;
    onProductChange?.(product.name as ProductKey);
    window.location.href = landingHref;
  };

  const handleSwitcherOpenChange = (open: boolean) => {
    setIsSwitcherOpen(open);
    setIsMenuOpened(open);
  };

  const handlePopoverOpenChange = (open: boolean) => {
    setIsMenuOpened(open);
  };

  /**
   * プロジェクト切り替えの遷移先 URL を解決する。
   * `newTab` が true のときは他基盤で開く必要があるケース
   * (KARTE mapping 欠落 / KARTE-only 行を QANT ホストから開く)。
   */
  const resolveProjectSwitchTarget = (params: {
    organizationKey: string;
    projectKey: string;
  }): { href: string; newTab: boolean } | undefined => {
    const org = data.organizations.find((o) => o.key === params.organizationKey);
    const project = org?.projects.find((p) => p.key === params.projectKey);
    if (!org || !project) return undefined;

    const landingHref = currentProductMenu ? resolveProjectSwitchLandingHref(currentProductMenu, buildHref) : undefined;

    if (data.selfPlatform === 'qant') {
      if (project.qantProjectId) {
        // account-only (current 欠落) や landing が cross-domain なときは
        // 当該基盤のデフォルト入口にフォールバックする（既定経路を無反応にしないため）。
        const base = landingHref ?? buildHref({ host: 'qant', path: '/', skipProjectParam: true }).href;
        const origin = typeof window === 'undefined' ? 'http://__internal__' : window.location.origin;
        const url = new URL(base, origin);
        url.searchParams.set('pid', project.qantProjectId);
        return { href: `${url.pathname}${url.search}${url.hash}`, newTab: false };
      }
      // KARTE-only: QANT プロダクト有無で /rightsupport/ と KARTE 本体を振り分ける
      if (!project.karteProjectId) return undefined;
      return resolveKarteOnlyProjectSwitchHref({
        buildHref,
        karteProjectId: project.karteProjectId,
        karteEnabledProducts: project.karteEnabledProducts,
      });
    }

    if (project.karteProjectId) {
      // account-only (current 欠落) や landing が cross-domain なときは
      // 当該基盤のデフォルト入口にフォールバックする（既定経路を無反応にしないため）。
      const base = landingHref ?? buildHref({ host: 'karte-io', path: '/', skipProjectParam: true }).href;
      const origin = typeof window === 'undefined' ? 'http://__internal__' : window.location.origin;
      const url = new URL(base, origin);
      url.searchParams.set('project', project.karteProjectId);
      return { href: `${url.pathname}${url.search}${url.hash}`, newTab: false };
    }

    // KARTE mapping 欠落: QANT 基盤で当該プロジェクトを開く
    if (!project.qantProjectId) return undefined;
    // skipProjectParam: このリンクの project は「対象行の project」であり、
    // buildHref が自動付与する現在の projectIds ベースの project とは異なる。
    const { href: qantHref } = buildHref({ host: 'qant', path: '/', skipProjectParam: true });
    const qantUrl = new URL(qantHref);
    qantUrl.searchParams.set('pid', project.qantProjectId);
    return { href: qantUrl.toString(), newTab: true };
  };

  const defaultOnProjectSwitch = (params: { projectId: string; organizationId: string }) => {
    const org = data.organizations.find((o) => o.qantOrganizationId === params.organizationId);
    const project = org?.projects.find((p) => p.qantProjectId === params.projectId);
    if (!org || !project) return;
    const target = resolveProjectSwitchTarget({ organizationKey: org.key, projectKey: project.key });
    if (!target) return;

    if (target.newTab) {
      window.open(target.href, '_blank', 'noopener,noreferrer');
      return;
    }

    window.location.href = target.href;
  };

  const handleProjectSwitch = onProjectSwitchProp ?? defaultOnProjectSwitch;

  const resolvedSubHeader = typeof subHeader === 'function' ? subHeader({ expanded, setIsMenuOpened }) : subHeader;

  const user = view.user;
  const userInitial = user.name.slice(0, 1);
  const roleLabel = data.user.roleLabel;

  const currentProjectKey = (() => {
    const projectIds = data.current?.projectIds;
    if (!projectIds) return undefined;
    for (const org of data.organizations) {
      for (const project of org.projects) {
        if (projectIds.qant && project.qantProjectId === projectIds.qant) return project.key;
        if (projectIds.karte && project.karteProjectId === projectIds.karte) return project.key;
      }
    }
    return undefined;
  })();

  const projectTrigger = (
    <button
      type="button"
      className={sourTwMerge(
        'flex w-full cursor-pointer items-center gap-x-2 rounded-lg px-2 py-2 text-left transition-colors hover:bg-action-subtle-hover',
      )}
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full border border-muted bg-default">
        <FontAwesomeIcon className="text-sm" icon={faBrowser} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-[11px] text-muted">{view.organization?.name ?? 'プロジェクトを選択'}</span>
        <Tooltip content={view.project?.name} side="right" className="z-20">
          <span className="truncate text-sm font-semibold text-primitive-black">{view.project?.name ?? '未選択'}</span>
        </Tooltip>
      </span>
      <FontAwesomeIcon icon={faAnglesUpDown} className="shrink-0 text-xs text-muted" />
    </button>
  );

  const userTrigger = (
    <button
      type="button"
      className={sourTwMerge(
        'flex w-full cursor-pointer items-center gap-x-2 rounded-lg px-2 py-2 text-left transition-colors hover:bg-action-subtle-hover',
      )}
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center">
        <span
          className="flex h-7 w-7 items-center justify-center overflow-hidden rounded-full text-xs font-bold text-primitive-white"
          style={user.avatarUrl ? undefined : { backgroundColor: '#4f88ee' }}
        >
          {user.avatarUrl ? <img src={user.avatarUrl} alt="" className="h-full w-full object-cover" /> : userInitial}
        </span>
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-semibold text-primitive-black">{user.name}</span>
        {roleLabel ? <span className="truncate text-[11px] text-muted">{roleLabel}</span> : null}
      </span>
    </button>
  );

  return (
    <SidebarShell
      expanded={expanded}
      onPointerEnter={() => {
        // ref は展開状態ではなくポインタの実位置なので、遅延させず即座に立てる
        isPointerOverRef.current = true;
        clearHoverExpandTimer();
        hoverExpandTimerRef.current = setTimeout(() => {
          hoverExpandTimerRef.current = null;
          setIsPointerOver(true);
        }, HOVER_EXPAND_DELAY_MS);
      }}
      onPointerLeave={() => {
        isPointerOverRef.current = false;
        // 展開待ちのまま離脱した場合は展開せずに終わる
        clearHoverExpandTimer();
        setIsPointerOver(false);
        // マウス操作で得たフォーカスは pointer leave で閉じる（クリック後のフォーカス残り対策）
        setIsFocusWithin(false);
      }}
      onFocusWithin={() => {
        // ポインタがナビ上にあるときのフォーカス（クリック等）は展開維持に使わない
        if (!isPointerOverRef.current) {
          setIsFocusWithin(true);
        }
      }}
      onFocusLeave={() => setIsFocusWithin(false)}
      className="border-r border-muted bg-subtle-light"
    >
      <div className="flex h-full flex-col" data-component="GlobalNavi">
        {currentProductMenu ? (
          <div className="px-2 pt-3">
            <ProductSwitcherPopover
              products={products}
              currentProductName={currentProduct}
              open={isSwitcherOpen}
              onOpenChange={handleSwitcherOpenChange}
              onSelectProduct={onSelectProduct}
              hrefResolver={hrefResolver}
              resolveProductHref={(product) => resolveProductLandingHref(product, buildHref)}
              onMenuClick={onMenuClick}
              locale={locale}
              trigger={
                <button
                  type="button"
                  className={sourTwMerge(
                    'flex w-full cursor-pointer items-center gap-x-2 rounded-lg px-2 py-2 text-left transition-colors hover:bg-action-subtle-hover',
                  )}
                >
                  <span className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-muted bg-default text-default">
                    <L1Icon icon={currentProductMenu.icon} className="size-[18px]" />
                    {showProductEnvironment ? <ProductEnvironmentBadge environment={productEnvironment} locale={locale} /> : null}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm font-semibold text-primitive-black">
                    {tx(currentProductMenu.label, locale)}
                    {showProductEnvironment ? tx(VOC_ENVIRONMENT_LABELS[productEnvironment], locale) : null}
                  </span>
                  <FontAwesomeIcon icon={faAnglesUpDown} className="shrink-0 text-xs text-muted" />
                </button>
              }
            />
          </div>
        ) : null}

        {resolvedSubHeader ? <div className="px-2 pt-2">{resolvedSubHeader}</div> : null}

        {currentProductMenu ? (
          <div className="flex-1 overflow-y-auto overflow-x-hidden px-2 pt-3 pb-4">
            <MenuGroupList
              groups={currentProductMenu.menuGroups}
              activeMenuName={activeMenuName}
              hrefResolver={hrefResolver}
              onMenuClick={onMenuClick}
              locale={locale}
              hideFirstGroupLabel
              expanded={expanded}
              onMenuPopoverOpenChange={setIsMenuOpened}
            />
          </div>
        ) : (
          <div className="flex-1" />
        )}

        <div className="px-2 pb-1">
          {data.organizations.length > 0 ? (
            <ProjectSwitcherPopover
              organizations={data.organizations}
              currentProjectKey={currentProjectKey}
              selfPlatform={data.selfPlatform}
              buildHref={buildHref}
              trigger={projectTrigger}
              onOpenChange={handlePopoverOpenChange}
              onProjectSwitch={handleProjectSwitch}
              resolveProjectHref={resolveProjectSwitchTarget}
            />
          ) : (
            projectTrigger
          )}
        </div>

        <div className="px-2 pb-3">
          <UserAccountPopover
            grants={data.user.grants}
            selfPlatform={data.selfPlatform}
            requiresCrossPlatformLogout={data.requiresCrossPlatformLogout}
            buildHref={buildHref}
            trigger={userTrigger}
            onOpenChange={handlePopoverOpenChange}
            grantActions={grantActions}
            logoutHelpHref={logoutHelpHref}
          />
        </div>
      </div>
    </SidebarShell>
  );
}
